/**
 * HubAccountDetailPage — BUSINESS-AUTONOMY Phase 7b.
 *
 * Renders the per-account cross-department timeline. Every workflow_items
 * row sharing this account_id (UUID — minted by sales-lead-qualifier on
 * verdict=demo) is grouped by department, with chronological detail
 * inside each group. The route exists so super_admins can audit the
 * full lead → handoff → onboarding → bug → marketing-attribution chain
 * the autonomy stack produces.
 */
import React, { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Card, Pill } from './components'
import { hubAccounts, type HubAccountDetail, type HubAccountWorkflowRow } from './api'
import { formatDateTime } from '@/utils/dateUtils'

const DEPT_ORDER = ['marketing', 'sales', 'cs', 'support', 'qa', 'eng']

function deptLabel(d: string): string {
  switch (d) {
    case 'cs':
      return 'Customer Success'
    case 'qa':
      return 'QA'
    case 'eng':
      return 'Engineering'
    default:
      return d.charAt(0).toUpperCase() + d.slice(1)
  }
}

function formatDate(s: string | null | undefined): string {
  return formatDateTime(s)
}

function rowVariant(row: HubAccountWorkflowRow): 'live' | 'info' | 'err' | 'warn' | 'ghost' {
  if (row.status === 'done') return 'live'
  if (row.status === 'in_review') return 'warn'
  if (row.status === 'blocked' || row.status === 'cancelled') return 'err'
  if (row.status === 'in_progress') return 'info'
  return 'ghost'
}

function priorityVariant(p: string): 'live' | 'info' | 'err' | 'warn' | 'ghost' {
  if (p === 'p0') return 'err'
  if (p === 'p1') return 'warn'
  if (p === 'p2') return 'info'
  return 'ghost'
}

export default function HubAccountDetailPage() {
  const { id } = useParams<{ id: string }>()
  const [detail, setDetail] = useState<HubAccountDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!id) return
    setLoading(true)
    hubAccounts
      .get(id)
      .then(d => {
        setDetail(d)
        setError(null)
      })
      .catch(err => {
        setError(err instanceof Error ? err.message : 'Failed to load account')
      })
      .finally(() => setLoading(false))
  }, [id])

  if (!id) {
    return (
      <div style={{ padding: 24 }}>
        <p>Missing account id.</p>
      </div>
    )
  }

  if (loading) {
    return (
      <div style={{ padding: 24 }}>
        <p>Loading account…</p>
      </div>
    )
  }

  if (error || !detail) {
    return (
      <div style={{ padding: 24 }}>
        <Card>
          <div style={{ padding: 16 }}>
            <h2 style={{ margin: 0 }}>Account not found</h2>
            <p style={{ marginTop: 8, opacity: 0.8 }}>{error || `No workflow rows carry account_id ${id}`}</p>
            <Link to="/sales">← Back to Sales</Link>
          </div>
        </Card>
      </div>
    )
  }

  const grouped: Record<string, HubAccountWorkflowRow[]> = {}
  for (const row of detail.workflow_items) {
    const key = row.department || 'unknown'
    if (!grouped[key]) grouped[key] = []
    grouped[key].push(row)
  }
  const orderedDepts = [
    ...DEPT_ORDER.filter(d => grouped[d]),
    ...Object.keys(grouped).filter(d => !DEPT_ORDER.includes(d)),
  ]

  return (
    <div style={{ padding: 24, display: 'grid', gap: 16 }}>
      <div>
        <Link to="/sales" style={{ fontSize: 13 }}>← Sales</Link>
        <h1 style={{ margin: '8px 0 4px', fontFamily: 'monospace' }}>Account {id}</h1>
        <div style={{ display: 'flex', gap: 12, fontSize: 13, opacity: 0.8, flexWrap: 'wrap' }}>
          <span>First seen: {formatDate(detail.first_seen_at)}</span>
          <span>•</span>
          <span>Last activity: {formatDate(detail.last_activity_at)}</span>
          <span>•</span>
          <span>{detail.row_count} workflow rows</span>
          <span>•</span>
          <span>Depts: {detail.departments_touched.join(', ') || 'none'}</span>
        </div>
      </div>

      {orderedDepts.map(dept => (
        <Card key={dept}>
          <div style={{ padding: 16 }}>
            <h2 style={{ margin: 0, fontSize: 15 }}>{deptLabel(dept)}</h2>
            <div style={{ marginTop: 12, display: 'grid', gap: 8 }}>
              {grouped[dept].map(row => (
                <div
                  key={row.id}
                  style={{
                    padding: 12,
                    borderRadius: 8,
                    background: 'var(--card-bg-2, rgba(255,255,255,0.02))',
                    border: '1px solid var(--card-border, rgba(255,255,255,0.08))',
                  }}
                >
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Pill variant={rowVariant(row)}>{row.status}</Pill>
                    <Pill variant={priorityVariant(row.priority)}>{row.priority}</Pill>
                    <Pill variant="ghost">{row.type}</Pill>
                    {row.handoff_from && row.handoff_to && (
                      <Pill variant="info">
                        {row.handoff_from} → {row.handoff_to}
                      </Pill>
                    )}
                    <span style={{ marginLeft: 'auto', fontSize: 12, opacity: 0.7 }}>
                      {formatDate(row.created_at)}
                    </span>
                  </div>
                  <div style={{ marginTop: 6, fontWeight: 500 }}>{row.title}</div>
                  <div style={{ marginTop: 4, fontSize: 12, opacity: 0.7, fontFamily: 'monospace' }}>
                    {row.row_id || row.id}
                    {row.assigned_agent ? ` • ${row.assigned_agent}` : ''}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      ))}
    </div>
  )
}
